/**
 * The brief card (docs/SPEC.md §5.5): the brief as it stands, two columns on
 * a desktop and a list on a phone, every line with its provenance; the lines
 * the API takes (`backend/ads/brief_card.py::EDITABLE`) are editable in place —
 * **Edit** opens the line's own box, Save sends `PATCH …/brief {field, value}`
 * and the line then reads *you said*. The model's suggestions are offered with
 * Accept and Drop, each with what accepting it does (words on screen, what a
 * scene shows). What only the person can give is asked on its own line: an
 * end card nobody named says "No name yet — add it", which opens the line's
 * box (§5.4, P28-T16). A change the brief's rules refuse
 * (`brief_does_not_fit`) is said next to the line it concerns. The look
 * follows the brand kit chosen in the composer and has no box of its own.
 *
 * What a scene shows and what a cast member is (`what`) are the model's
 * words, in which a reference may stand (`photo:2`, SYSTEM.md rule 17): the
 * card shows each in plain words ("your photo 2", the Object's name,
 * `copy/plain.ts::readRefs`) and a saved box gives it back its references
 * (`refsBack`).
 *
 * The same card is the brief stage's (`BriefStage`) and, once the film
 * exists, a fold of the film page (`FilmPage`, §5.5 P28): there an edit or an
 * Accept is a change of the film written by code (the API answers 202 with
 * the change), and the lines the film cannot take after it is made (length
 * and shape, sound: `after_film`) have no box.
 */
import { type ReactNode, useState } from "react";
import {
  AD_ASPECTS,
  AD_LENGTHS,
  type Brief,
  type BriefField,
  type BriefScene,
  type Music,
  type VoiceCharacter,
} from "../api/ads";
import { ApiError } from "../api/client";
import { sentenceFor } from "../copy/errors";
import {
  ASPECT_NAMES,
  CARD,
  CONVERSATION,
  MUSIC_NAMES,
  VOICE_NAMES,
  provenanceOf,
  say,
} from "../copy/brief";
import { readRefs, refsBack } from "../copy/plain";
import { Palette } from "../ui/Chips";
import { Button } from "../ui";

export type EditLine = (field: BriefField, value: unknown) => Promise<unknown>;

/** The names of the brief's references the card knows (`objects:<id>` → the
 *  Object's name), for `readRefs`. */
export type RefNames = Record<string, string>;

/** The names the cast panel knows for the brief's references: each cast
 *  member's source (`objects:`, `import:`, `design:`) by the name its row
 *  shows (the panel's rows are keyed by the member's id). */
export function refNames(
  brief: Brief,
  rows: readonly { key: string; name: string }[],
): RefNames {
  const names: RefNames = {};
  for (const member of brief.cast) {
    const row = rows.find((r) => r.key === member.id);
    if (row && /^(objects|import|design):/.test(member.source))
      names[member.source] = row.name;
  }
  return names;
}

// ------------------------------------------------------------ words of a line

export function moodWords(brief: Brief): string {
  return brief.mood.words.join(" · ");
}

export function lookWords(brief: Brief, kitName: string | null): string {
  const parts: string[] = [];
  if (brief.look.brand_kit_id && kitName) {
    parts.push(say(CARD.lookKit, { name: kitName }));
  }
  const n = brief.look.palette.length;
  if (n > 0)
    parts.push(n === 1 ? CARD.lookColour : say(CARD.lookColours, { n }));
  parts.push(...brief.look.fonts);
  return parts.join(" · ");
}

/** The Ends with line's words: the web address (else the name), the
 *  tagline; a name nobody gave is asked for on the line (`EndValue`). */
export function endWords(brief: Brief): string {
  const end = brief.end_card;
  const host = end.url
    ? end.url.replace(/^https?:\/\//, "").replace(/\/$/, "")
    : null;
  return [host ?? end.name, end.tagline ? `"${end.tagline}"` : null]
    .filter(Boolean)
    .join(" · ");
}

/** The Ends with line's value: with no name, "No name yet — add it", which
 *  opens the line's box (§5.4, P28-T16); "No name yet" where the card cannot
 *  be changed. */
function EndValue({
  brief,
  editable,
  onAdd,
}: {
  brief: Brief;
  editable: boolean;
  onAdd: () => void;
}) {
  const words = endWords(brief);
  if (brief.end_card.name) return <>{words}</>;
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-2">
      {editable ? (
        <button
          type="button"
          className="text-brand underline"
          data-testid="end-add-name"
          onClick={onAdd}
        >
          {CARD.endAddName}
        </button>
      ) : (
        <span className="text-slate-500">{CARD.endNoName}</span>
      )}
      {words ? <span>{` · ${words}`}</span> : null}
    </span>
  );
}

export function soundWords(brief: Brief): string {
  const s = brief.sound;
  const music =
    s.music === "none"
      ? CARD.noMusic
      : s.music === "decide"
        ? CARD.musicDecide
        : say(CARD.music, { music: MUSIC_NAMES[s.music] ?? s.music });
  const voice =
    s.voice.character === "none"
      ? CARD.noVoice
      : say(CARD.voice, {
          character: VOICE_NAMES[s.voice.character] ?? s.voice.character,
        });
  return [music, voice, s.sfx ? CARD.sfx : null].filter(Boolean).join(" · ");
}

/** The provenance of a list: one name when every entry agrees, else each
 *  once, in order. */
function listProvenance(froms: string[]): string | null {
  const names = [...new Set(froms.map(provenanceOf).filter(Boolean))];
  return names.length > 0 ? names.join(" · ") : null;
}

// ----------------------------------------------------------------- editors

function EditorShell({
  label,
  onSave,
  onCancel,
  children,
}: {
  label: string;
  onSave: () => Promise<unknown>;
  onCancel: () => void;
  children: ReactNode;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="flex flex-col gap-2"
      aria-label={label}
      onSubmit={(e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        onSave().then(
          () => {
            setSaving(false);
            onCancel();
          },
          (err: unknown) => {
            setSaving(false);
            setError(sentenceFor(err));
          },
        );
      }}
    >
      {children}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" variant="primary" disabled={saving}>
          {saving ? CARD.saving : CARD.save}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          {CARD.cancel}
        </Button>
      </div>
      {error ? (
        <p role="alert" className="m-0 text-sm text-slate-700">
          {error}
        </p>
      ) : null}
    </form>
  );
}

function TextEditor({
  label,
  initial,
  hint,
  onSave,
  onCancel,
}: {
  label: string;
  initial: string;
  hint?: string;
  onSave: (value: string) => Promise<unknown>;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <EditorShell
      label={label}
      onSave={() => onSave(value.trim())}
      onCancel={onCancel}
    >
      <input
        className="tb-input"
        aria-label={label}
        autoFocus
        value={value}
        placeholder={hint}
        maxLength={500}
        onChange={(e) => setValue(e.target.value)}
      />
    </EditorShell>
  );
}

/** The small field Accept opens on a suggestion that asks for a value only the
 *  person can give (a price, a phone number…): what they type is sent as the
 *  caption (2026-10-23 fix(brief) accept a value). */
function ValueField({
  label,
  disabled,
  onSend,
  onCancel,
}: {
  label: string;
  disabled: boolean;
  onSend: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState("");
  const words = value.trim();
  return (
    <form
      className="flex w-full flex-wrap items-end gap-2"
      data-testid="suggestion-value"
      onSubmit={(e) => {
        e.preventDefault();
        if (words && !disabled) onSend(words);
      }}
    >
      <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs text-slate-500">
        {label}
        <input
          className="tb-input"
          autoFocus
          value={value}
          maxLength={120}
          onChange={(e) => setValue(e.target.value)}
        />
      </label>
      <span className="flex gap-2">
        <Button
          type="submit"
          size="sm"
          variant="secondary"
          disabled={disabled || !words}
        >
          {CARD.needsAdd}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          {CARD.cancel}
        </Button>
      </span>
    </form>
  );
}

function LengthEditor({
  brief,
  onEdit,
  onCancel,
}: {
  brief: Brief;
  onEdit: EditLine;
  onCancel: () => void;
}) {
  const [length, setLength] = useState(brief.length_s);
  const [aspect, setAspect] = useState(brief.aspect);
  return (
    <EditorShell
      label={CARD.length}
      onSave={async () => {
        if (length !== brief.length_s) await onEdit("length_s", length);
        if (aspect !== brief.aspect) await onEdit("aspect", aspect);
      }}
      onCancel={onCancel}
    >
      <div className="flex flex-wrap gap-2">
        <select
          className="tb-input w-auto"
          aria-label={CARD.length}
          value={length}
          onChange={(e) => setLength(Number(e.target.value))}
        >
          {AD_LENGTHS.map((v) => (
            <option key={v} value={v}>
              {v} s
            </option>
          ))}
        </select>
        <select
          className="tb-input w-auto"
          aria-label="Aspect"
          value={aspect}
          onChange={(e) => setAspect(e.target.value)}
        >
          {AD_ASPECTS.map((v) => (
            <option key={v} value={v}>
              {ASPECT_NAMES[v]}
            </option>
          ))}
        </select>
      </div>
    </EditorShell>
  );
}

function EndEditor({
  brief,
  onEdit,
  onCancel,
}: {
  brief: Brief;
  onEdit: EditLine;
  onCancel: () => void;
}) {
  const [name, setName] = useState(brief.end_card.name ?? "");
  const [url, setUrl] = useState(brief.end_card.url ?? "");
  const [tagline, setTagline] = useState(brief.end_card.tagline ?? "");
  return (
    <EditorShell
      label={CARD.end_card}
      onSave={() =>
        onEdit("end_card", {
          name: name.trim() || null,
          url: url.trim() || null,
          tagline: tagline.trim() || null,
        })
      }
      onCancel={onCancel}
    >
      <input
        className="tb-input"
        aria-label={CARD.endName}
        placeholder={CARD.endName}
        value={name}
        autoFocus
        onChange={(e) => setName(e.target.value)}
      />
      <input
        className="tb-input"
        aria-label={CARD.endUrl}
        placeholder={CARD.endUrl}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
      />
      <input
        className="tb-input"
        aria-label={CARD.endTagline}
        placeholder={CARD.endTagline}
        value={tagline}
        onChange={(e) => setTagline(e.target.value)}
      />
    </EditorShell>
  );
}

/** A scene as the API takes it back: the brief's own fields, `from` left to
 *  the server (it sets `said`). */
function sceneOut(scene: BriefScene): Omit<BriefScene, "from"> {
  const { from: _from, ...rest } = scene;
  void _from;
  return rest;
}

function ScenesEditor({
  brief,
  names,
  onEdit,
  onCancel,
}: {
  brief: Brief;
  names: RefNames;
  onEdit: EditLine;
  onCancel: () => void;
}) {
  // each scene's `what` in plain words, and the references it held
  const [read] = useState(() =>
    brief.scenes.map((s) => readRefs(s.what, names)),
  );
  const [scenes, setScenes] = useState(() =>
    brief.scenes.map((s, i) => ({ ...s, what: read[i].text })),
  );
  // The captions as typed; split into lines when saved.
  const [captions, setCaptions] = useState(() =>
    brief.scenes.map((s) => s.captions.join("\n")),
  );
  const patch = (i: number, over: Partial<BriefScene>) =>
    setScenes((list) => list.map((s, j) => (j === i ? { ...s, ...over } : s)));
  const lines = (text: string) =>
    text
      .split("\n")
      .map((c) => c.trim())
      .filter(Boolean)
      .slice(0, 4);
  return (
    <EditorShell
      label={CARD.scenes}
      onSave={() =>
        onEdit(
          "scenes",
          scenes.map((s, i) =>
            sceneOut({
              ...s,
              what: refsBack(s.what, read[i].refs),
              captions: lines(captions[i]),
            }),
          ),
        )
      }
      onCancel={onCancel}
    >
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        {scenes.map((scene, i) => (
          <li key={scene.id} className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-slate-500">
              {i + 1} · {scene.seconds} s
            </span>
            <input
              className="tb-input"
              aria-label={`${CARD.sceneName} ${i + 1}`}
              value={scene.name}
              maxLength={80}
              onChange={(e) => patch(i, { name: e.target.value })}
            />
            <textarea
              className="tb-input min-h-[64px]"
              aria-label={`${CARD.sceneWhat} ${i + 1}`}
              value={scene.what}
              maxLength={600}
              onChange={(e) => patch(i, { what: e.target.value })}
            />
            <textarea
              className="tb-input min-h-[44px]"
              aria-label={`${CARD.sceneCaptions} ${i + 1}`}
              placeholder={CARD.sceneCaptions}
              value={captions[i]}
              onChange={(e) => {
                const text = e.target.value;
                setCaptions((list) => list.map((c, j) => (j === i ? text : c)));
              }}
            />
          </li>
        ))}
      </ol>
    </EditorShell>
  );
}

function CastEditor({
  brief,
  names,
  onEdit,
  onCancel,
}: {
  brief: Brief;
  names: RefNames;
  onEdit: EditLine;
  onCancel: () => void;
}) {
  const [read] = useState(() => brief.cast.map((m) => readRefs(m.what, names)));
  const [cast, setCast] = useState(() =>
    brief.cast.map((m, i) => ({ ...m, what: read[i].text })),
  );
  return (
    <EditorShell
      label={CARD.cast}
      onSave={() =>
        onEdit(
          "cast",
          cast.map(({ id, what, source }, i) => ({
            id,
            what: refsBack(what, read[i].refs),
            source,
          })),
        )
      }
      onCancel={onCancel}
    >
      {cast.map((member, i) => (
        <input
          key={member.id}
          className="tb-input"
          aria-label={`${CARD.castWhat}: ${member.id}`}
          value={member.what}
          maxLength={200}
          onChange={(e) =>
            setCast((list) =>
              list.map((m, j) =>
                j === i ? { ...m, what: e.target.value } : m,
              ),
            )
          }
        />
      ))}
    </EditorShell>
  );
}

function SoundEditor({
  brief,
  onEdit,
  onCancel,
}: {
  brief: Brief;
  onEdit: EditLine;
  onCancel: () => void;
}) {
  const [music, setMusic] = useState<Music>(brief.sound.music);
  const [voice, setVoice] = useState<VoiceCharacter>(
    brief.sound.voice.character,
  );
  return (
    <EditorShell
      label={CARD.sound}
      onSave={() =>
        onEdit("sound", {
          music,
          voice: { ...brief.sound.voice, character: voice },
        })
      }
      onCancel={onCancel}
    >
      <div className="flex flex-wrap gap-2">
        <label className="flex items-center gap-2 text-sm">
          {CARD.musicLabel}
          <select
            className="tb-input w-auto"
            value={music}
            onChange={(e) => setMusic(e.target.value as Music)}
          >
            {(["calm", "energetic", "none", "decide"] as const).map((m) => (
              <option key={m} value={m}>
                {MUSIC_NAMES[m]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          {CARD.voiceLabel}
          <select
            className="tb-input w-auto"
            value={voice}
            onChange={(e) => setVoice(e.target.value as VoiceCharacter)}
          >
            {(["warm", "clear", "none"] as const).map((v) => (
              <option key={v} value={v}>
                {VOICE_NAMES[v]}
              </option>
            ))}
          </select>
        </label>
      </div>
    </EditorShell>
  );
}

// -------------------------------------------------------------------- lines

interface LineSpec {
  key: string;
  label: string;
  value: ReactNode;
  from: string | null;
  /** The box that edits this line; absent for a line that is not edited. */
  editor?: (close: () => void) => ReactNode;
}

/** The card's line a refused change concerns (`detail.field` of
 *  `brief_does_not_fit`: a field of the brief), or null. */
export function refusedLine(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== "brief_does_not_fit") {
    return null;
  }
  const detail =
    error.detail && typeof error.detail === "object"
      ? (error.detail as Record<string, unknown>)
      : {};
  const field = typeof detail.field === "string" ? detail.field : null;
  if (field === "length_s" || field === "aspect") return "length";
  return field;
}

/** A class list without its `md:` part: the card laid out as a list at any
 *  width (a narrow column, the film page's fold). */
function listOnly(classes: string, compact: boolean): string {
  return compact
    ? classes
        .split(" ")
        .filter((c) => !c.startsWith("md:"))
        .join(" ")
    : classes;
}

function Line({
  spec,
  editable,
  open,
  onOpen,
  onClose,
  notice = null,
  compact = false,
}: {
  spec: LineSpec;
  editable: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** A refused change of the card, said next to the line it concerns. */
  notice?: string | null;
  compact?: boolean;
}) {
  const md = (classes: string) => listOnly(classes, compact);
  return (
    <div
      className={md(
        "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 border-t border-slate-100 py-3 first:border-t-0 md:grid-cols-[7.5rem_minmax(0,1fr)_auto] md:items-baseline md:gap-4",
      )}
      data-testid={`line-${spec.key}`}
    >
      <dt
        className={md(
          "col-start-1 row-start-1 text-xs font-semibold uppercase tracking-wide text-slate-500 md:text-sm md:normal-case md:tracking-normal",
        )}
      >
        {spec.label}
      </dt>
      <dd
        className={md(
          "col-span-2 row-start-2 m-0 min-w-0 md:col-span-1 md:col-start-2 md:row-start-1 text-slate-900",
        )}
      >
        {open && spec.editor ? spec.editor(onClose) : spec.value}
      </dd>
      <div
        className={md(
          "col-start-2 row-start-1 flex items-baseline justify-end gap-3 md:col-start-3",
        )}
      >
        {spec.from ? (
          <span
            className="text-xs text-slate-500"
            data-testid={`from-${spec.key}`}
          >
            {spec.from}
          </span>
        ) : null}
        {editable && spec.editor && !open ? (
          <button
            type="button"
            className="inline-flex min-h-[44px] items-center text-sm text-brand underline md:min-h-0"
            aria-label={`${CARD.edit} ${spec.label}`}
            onClick={onOpen}
          >
            {CARD.edit}
          </button>
        ) : null}
      </div>
      {notice ? (
        <p
          role="alert"
          className={md("col-span-2 m-0 text-sm text-slate-700 md:col-span-3")}
        >
          {notice}
        </p>
      ) : null}
    </div>
  );
}

function readDropped(key: string | null): string[] {
  if (!key) return [];
  try {
    const raw = window.localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function writeDropped(key: string | null, list: string[]) {
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(list));
  } catch {
    // a browser without storage forgets the drop on reload; nothing else
  }
}

export function BriefCard({
  brief,
  kitName,
  editable,
  final,
  afterFilm = false,
  compact = false,
  busy = false,
  onEdit,
  onAccept,
  onYouDecide,
  dropKey = null,
  actions,
  refused = null,
  names = {},
}: {
  brief: Brief;
  /** The brand kit's name, for the look line. */
  kitName: string | null;
  /** Lines may be edited (while no turn or change runs). */
  editable: boolean;
  /** The brief is final: You decide is no longer offered. */
  final: boolean;
  /** The film exists: length, shape and sound are said in the conversation
   *  instead (the card's 400 `after_film`). */
  afterFilm?: boolean;
  /** Laid out as a list at any width (a narrow column). */
  compact?: boolean;
  /** A turn is running: nothing can be sent. */
  busy?: boolean;
  onEdit: EditLine;
  /** Accept a suggestion; `value` is what one that asks for a value (`needs`)
   *  is accepted with: it becomes the caption, never the suggestion's words. */
  onAccept: (index: number, value?: string) => void;
  onYouDecide: () => void;
  /** Where the suggestions the person dropped are remembered (this browser
   *  only: the API keeps a suggestion until it is accepted, and an unaccepted
   *  one is never a caption, §5.2 (6)). */
  dropKey?: string | null;
  /** More buttons for the card's head. */
  actions?: ReactNode;
  /** The error of the last Accept, said next to the line it concerns (else
   *  under the suggestions). */
  refused?: unknown;
  /** The names of the references a `what` may hold (`objects:<id>` → the
   *  Object's name); without one, the reference's own words. */
  names?: RefNames;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [dropped, setDropped] = useState<string[]>(() => readDropped(dropKey));
  /** The suggestion whose value is being asked for (Accept on one with `needs`). */
  const [asking, setAsking] = useState<number | null>(null);
  const close = () => setOpen(null);
  const save = (field: BriefField) => (value: unknown) => onEdit(field, value);
  const withClose =
    (
      render: (props: {
        brief: Brief;
        names: RefNames;
        onEdit: EditLine;
        onCancel: () => void;
      }) => ReactNode,
    ) =>
    (doClose: () => void) =>
      render({ brief, names, onEdit, onCancel: doClose });

  const lines: LineSpec[] = [
    {
      key: "subject",
      label: CARD.subject,
      value: brief.subject.text,
      from: provenanceOf(brief.subject.from),
      editor: (c) => (
        <TextEditor
          label={CARD.subject}
          initial={brief.subject.text}
          onSave={save("subject")}
          onCancel={c}
        />
      ),
    },
    {
      key: "promise",
      label: CARD.promise,
      value: brief.promise.text,
      from: provenanceOf(brief.promise.from),
      editor: (c) => (
        <TextEditor
          label={CARD.promise}
          initial={brief.promise.text}
          onSave={save("promise")}
          onCancel={c}
        />
      ),
    },
    {
      key: "audience",
      label: CARD.audience,
      value: brief.audience.text,
      from: provenanceOf(brief.audience.from),
      editor: (c) => (
        <TextEditor
          label={CARD.audience}
          initial={brief.audience.text}
          onSave={save("audience")}
          onCancel={c}
        />
      ),
    },
    {
      key: "mood",
      label: CARD.mood,
      value: moodWords(brief),
      from: provenanceOf(brief.mood.from),
      editor: (c) => (
        <TextEditor
          label={CARD.mood}
          initial={brief.mood.words.join(", ")}
          hint={CARD.moodHint}
          onSave={save("mood")}
          onCancel={c}
        />
      ),
    },
    {
      key: "length",
      label: CARD.length,
      value: `${brief.length_s} s · ${brief.aspect}`,
      from: provenanceOf("said"),
      editor: afterFilm ? undefined : withClose((p) => <LengthEditor {...p} />),
    },
    {
      key: "look",
      label: CARD.look,
      value: (
        <span className="inline-flex flex-wrap items-center gap-2">
          {brief.look.palette.length > 0 ? (
            <Palette colours={brief.look.palette} />
          ) : null}
          <span>{lookWords(brief, kitName)}</span>
        </span>
      ),
      from: provenanceOf(brief.look.from),
    },
    {
      key: "end_card",
      label: CARD.end_card,
      value: (
        <EndValue
          brief={brief}
          editable={editable && !busy}
          onAdd={() => setOpen("end_card")}
        />
      ),
      from: provenanceOf(brief.end_card.from),
      editor: withClose((p) => <EndEditor {...p} />),
    },
    {
      key: "scenes",
      label: CARD.scenes,
      value: (
        <ol className="m-0 flex list-none flex-col gap-0.5 p-0">
          {brief.scenes.map((scene, i) => (
            <li key={scene.id}>
              {say(CARD.sceneLine, {
                n: i + 1,
                name: scene.name,
                seconds: scene.seconds,
              })}
            </li>
          ))}
        </ol>
      ),
      from: listProvenance(brief.scenes.map((s) => s.from)),
      editor: withClose((p) => <ScenesEditor {...p} />),
    },
    {
      key: "cast",
      label: CARD.cast,
      value: brief.cast.map((m) => readRefs(m.what, names).text).join(" · "),
      from: listProvenance(brief.cast.map((m) => m.from)),
      editor:
        brief.cast.length > 0
          ? withClose((p) => <CastEditor {...p} />)
          : undefined,
    },
    {
      key: "sound",
      label: CARD.sound,
      value: soundWords(brief),
      from: provenanceOf(brief.sound.from),
      editor: afterFilm ? undefined : withClose((p) => <SoundEditor {...p} />),
    },
  ];

  const suggestions = brief.suggestions
    .map((s, index) => ({ ...s, index }))
    .filter((s) => !dropped.includes(s.text));
  const refusedSentence = refused ? sentenceFor(refused) : null;
  const refusedAt = refused ? refusedLine(refused) : null;
  const atLine = lines.some((spec) => spec.key === refusedAt)
    ? refusedAt
    : null;

  return (
    <section
      aria-label={CARD.title}
      className="tb-card tb-card-plain flex flex-col gap-2 p-5 md:p-6"
      data-testid="brief-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="m-0 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
          {CARD.title}
        </h2>
        <div className="flex flex-wrap gap-2">
          {!final ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              title={CONVERSATION.youDecideHint}
              onClick={onYouDecide}
            >
              {CONVERSATION.youDecide}
            </Button>
          ) : null}
          {actions}
        </div>
      </div>
      <dl className="m-0">
        {lines.map((spec) => (
          <Line
            key={spec.key}
            spec={spec}
            editable={editable && !busy}
            open={open === spec.key}
            onOpen={() => setOpen(spec.key)}
            onClose={close}
            notice={spec.key === atLine ? refusedSentence : null}
            compact={compact}
          />
        ))}
        {editable && suggestions.length > 0 ? (
          <div
            className={listOnly(
              "flex flex-col gap-1 border-t border-slate-100 py-3 md:grid md:grid-cols-[7.5rem_minmax(0,1fr)] md:gap-4",
              compact,
            )}
            data-testid="line-suggested"
          >
            <dt
              className={listOnly(
                "text-xs font-semibold uppercase tracking-wide text-slate-500 md:text-sm md:normal-case md:tracking-normal",
                compact,
              )}
            >
              {CARD.suggested}
            </dt>
            <dd className="m-0 flex flex-col gap-2">
              {suggestions.map(({ text, kind, index, needs }) => (
                <div
                  key={`${index}-${text}`}
                  className="flex flex-wrap items-baseline justify-between gap-2"
                  data-testid="suggestion"
                  data-kind={kind}
                >
                  <span className="flex flex-col gap-0.5">
                    <span className="text-xs text-slate-500">
                      {CARD.suggestionKind[kind] ?? CARD.suggestionKind.staging}
                    </span>
                    <span className="text-slate-800">"{text}"</span>
                  </span>
                  {needs && asking === index ? (
                    <ValueField
                      label={CARD.needs[needs]}
                      disabled={!editable || busy}
                      onSend={(value) => {
                        setAsking(null);
                        onAccept(index, value);
                      }}
                      onCancel={() => setAsking(null)}
                    />
                  ) : null}
                  <span
                    className="flex gap-2"
                    hidden={Boolean(needs) && asking === index}
                  >
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={!editable || busy}
                      onClick={() =>
                        needs ? setAsking(index) : onAccept(index)
                      }
                    >
                      {CARD.accept}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const next = [...dropped, text];
                        setDropped(next);
                        writeDropped(dropKey, next);
                      }}
                    >
                      {CARD.drop}
                    </Button>
                  </span>
                </div>
              ))}
              {refusedSentence && atLine === null ? (
                <p role="alert" className="m-0 text-sm text-slate-700">
                  {refusedSentence}
                </p>
              ) : null}
            </dd>
          </div>
        ) : null}
      </dl>
      {!editable && final ? (
        <p className="m-0 text-xs text-slate-500">{CARD.locked}</p>
      ) : null}
    </section>
  );
}
